import React, { useState, useEffect, useCallback } from 'react';
import { Pagination, Table, TablePaginationConfig } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import { CaseResult, StaffCaseSearchContainerCaseSearchResultFieldsFragment } from '../../../../generated';
import { FormatDate } from '../../../../constants';
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface';

const getNavigationPath = (record: CaseResult) => {
  const currentLocation = window.location.pathname;
  return `${currentLocation}/${record.id}/case-type/${record.caseType}/active/v1/chat`;
};

interface CaseTypeMap {
  [key: string]: string;
}

const getFormattedCaseType = (caseType: string) => {
  const caseTypeMap: CaseTypeMap = {
    'credential-verification': 'Credential Verification',
    'send-report': 'Send Report',
    'id-verification': 'Identity Verification'
  };
  return caseTypeMap[caseType];
};

const getFormattedState = (state: string) => {
  return state
    .split(/(?=[A-Z])|[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface PaginationComponentProps {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, size?: number) => void;
}

const PaginationComponent: React.FC<PaginationComponentProps> = (props) => {
  return (
    <Pagination
      current={props.current}
      pageSize={props.pageSize}
      total={props.total}
      showTotal={(total) => `Count: ${total} cases`}
      onChange={props.onChange}
      onShowSizeChange={props.onChange}
      className="rounded-[10px] p-[15px] border border-[#f0f0f0] my-3"
    />
  );
};

interface CaseSearchTableProps {
  data: StaffCaseSearchContainerCaseSearchResultFieldsFragment[];
  sortValue: (value: string[]) => void;
  resultCount: number;
}

export const CaseSearchTable: React.FC<CaseSearchTableProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const tableChangeHandler = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<any> | SorterResult<any>[],
      _extra: TableCurrentDataSource<any>
    ) => {
      if (!Array.isArray(sorter) && sorter.order && sorter.columnKey) {
        const { order, columnKey } = sorter;
        const sort = order === 'ascend' ? 'asc' : 'desc';
        searchParams.set(`sortBy`, columnKey as string);
        searchParams.set(`orderBy`, sort);
        setSearchParams(searchParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    const size = parseInt(searchParams.get('pageSize') || '10', 10);

    setCurrentPage(page);
    setPageSize(size);
  }, [searchParams, setSearchParams]);

  const handlePaginationChange = (page: number, size?: number) => {
    const newSize = size || pageSize;
    setCurrentPage(page);
    setPageSize(newSize);
    searchParams.set('page', page.toString());
    searchParams.set('pageSize', newSize.toString());
    setSearchParams(searchParams);
  };

  const columns: any = [
    {
      title: 'Case Type',
      dataIndex: 'caseType',
      key: 'caseType',
      render: (text: string, record: any) => <Link to={getNavigationPath(record)}>{getFormattedCaseType(text)}</Link>,
      sorter: false
    },
    {
      title: 'Case Name',
      dataIndex: 'caseName',
      key: 'caseName',
      sorter: false
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (text: string) => {
        return getFormattedState(text);
      },
      sorter: false
    },
    {
      title: 'Case Manager',
      dataIndex: ['caseWorkerAssigned', 'displayName'],
      key: 'caseManager',
      render: (_text: any, record: any) => {
        return record?.caseWorkerAssigned?.displayName || 'Unassigned';
      },
      sorter: false
    },
    {
      title: 'Submitted At',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      sorter: true,
      defaultSortOrder: searchParams.get('sortBy') === 'submittedAt' ? (searchParams.get('orderBy') === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (text: string) => FormatDate(text)
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      defaultSortOrder:
        searchParams.get('sortBy') === 'updatedAt'
          ? searchParams.get('orderBy') === 'asc'
            ? 'ascend'
            : 'descend'
          : searchParams.get('sortBy') === 'submittedAt'
          ? undefined
          : 'descend',
      render: (text: string) => FormatDate(text)
    },
    {
      title: 'Contact',
      dataIndex: 'applicantName',
      key: 'applicantName',
      sorter: false
    },
    {
      title: 'Decision',
      dataIndex: 'decision',
      key: 'decision',
      render: (_text: any, record: any) => record?.decision?.result,
      sorter: false
    }
  ];

  return (
    <div className="flex flex-col w-full pt-[2px]">
      <PaginationComponent current={currentPage} pageSize={pageSize} total={props.resultCount} onChange={handlePaginationChange} />

      <Table
        className="w-full border border-[#f0f0f0] rounded-[10px] p-[15px] border-solid max-w-full"
        columns={columns}
        dataSource={props.data}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={false}
        onChange={tableChangeHandler}
        sortDirections={['ascend', 'descend']}
        rowHoverable={false}
      />
      <PaginationComponent current={currentPage} pageSize={pageSize} total={props.resultCount} onChange={handlePaginationChange} />
    </div>
  );
};
